import { useEffect, useState } from 'react'
import { lightImpactVibrate } from '../../utils/appBridge'

type ToggleOption = string

type TextTogglePropTypes = {
  currentToggleOption: ToggleOption
  toggleOptions: ToggleOption[]
  onToggleClick: (toggleOption: ToggleOption) => void
  toggleClassName?: string
  toggleColor?: string
}

export function TextToggle({
  currentToggleOption,
  toggleOptions,
  onToggleClick,
  toggleClassName,
  toggleColor
}: TextTogglePropTypes) {
  const [toggleOption, setToggleOption] = useState<ToggleOption>(toggleOptions?.[0])
  const [scale, setScale] = useState(1)

  useEffect(() => {
    setToggleOption(() => currentToggleOption)
  }, [currentToggleOption])

  const handleToggle = () => {
    if (toggleOptions?.length > 1) {
      setScale(0.95)
      setTimeout(() => setScale(1), 100)
      lightImpactVibrate()
    }
    const currentIndex = toggleOptions?.indexOf(toggleOption)
    const nextIndex = currentIndex === toggleOptions?.length - 1 ? 0 : currentIndex + 1
    const nextOption = toggleOptions[nextIndex]
    setToggleOption(() => nextOption)
    onToggleClick(nextOption)
  }

  return (
    <div
      onClick={handleToggle}
      role={toggleOptions?.length > 1 ? 'button' : undefined}
      tabIndex={toggleOptions?.length > 1 ? 0 : undefined}
      aria-label={
        toggleOptions?.length > 1
          ? `${toggleOption}, double tap to switch between ${toggleOptions.join(' and ')}`
          : toggleOption
      }
      onKeyDown={(e) => {
        if (toggleOptions?.length > 1 && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          handleToggle()
        }
      }}
      style={{
        transform: `scale(${scale})`,
        transition: 'transform 0.1s ease'
      }}
      className={`flex items-center justify-center gap-[4px] min-h-[24px] cursor-pointer ${toggleClassName ?? ''}`}
    >
      <div aria-hidden='true' className='pb-[1px]' style={{ color: toggleColor ?? '#6A6E6E' }}>
        {toggleOption}
      </div>
      {toggleOptions?.length > 1 && (
        <div aria-hidden='true' className='pt-[1px]'>
          <svg xmlns='http://www.w3.org/2000/svg' width='12' height='10' viewBox='0 0 12 10' fill='none'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M6.42426 0.664759C6.18995 0.44508 5.81005 0.44508 5.57574 0.664759L3.17576 3.03976C3.00416 3.20063 2.95282 3.44256 3.04569 3.65276C3.13856 3.86295 3.35734 4 3.60001 4H8.39999C8.64266 4 8.86144 3.86295 8.95431 3.65276C9.04718 3.44256 8.99584 3.20063 8.82424 3.03976L6.42426 0.664759ZM3.04569 6.34724C3.13856 6.13705 3.35734 6 3.60001 6H8.39999C8.64266 6 8.86144 6.13705 8.95431 6.34724C9.04718 6.55743 8.99584 6.79937 8.82424 6.96024L6.42426 9.3353C6.18995 9.5549 5.81005 9.5549 5.57574 9.3353L3.17576 6.96024C3.00416 6.79937 2.95282 6.55743 3.04569 6.34724Z'
              fill={toggleColor ?? '#6A6E6E'}
            />
          </svg>
        </div>
      )}
    </div>
  )
}
